import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Mail, MessageCircle, ArrowRight, Clock, Users, Zap } from 'lucide-react';
import { FloatingPaths } from '@LandingFunctionalDisplay/BackgroundPaths';
import TopPanel from '@CommonFunctionalDisplay/TopPanel';
import Footer from '@CommonFunctionalDisplay/Footer';
import useSEO from '@Hooks/useSEO';
import './CustomerSupportPage.scss';

const CHANNELS = [
    {
        icon: <BookOpen size={28} />,
        title: 'Documentation',
        desc: 'Comprehensive guides, API references, and step-by-step tutorials for every feature in KaizIQ Resolve.',
        cta: 'Browse Docs',
        ctaAction: 'docs',
        availability: 'Available 24/7',
    },
    {
        icon: <Mail size={28} />,
        title: 'Email Support',
        desc: 'Reach our support team directly with your questions, bug reports, or feature requests. We respond within one business day.',
        cta: 'Send Email',
        ctaAction: 'mailto:support@kaiziq.com',
        availability: 'Mon–Fri, 9am–6pm IST',
    },
    {
        icon: <MessageCircle size={28} />,
        title: 'Live Chat',
        desc: 'Chat with a real support agent for urgent issues and quick answers. Available on paid plans during business hours.',
        cta: 'Coming Soon',
        ctaAction: null,
        availability: 'Business hours',
        soon: true,
    },
];

const FAQS = [
    { q: 'How do I reset my password?', a: 'Go to the login page and click "Forgot Password". You will receive a reset link via email within a few minutes.' },
    { q: 'Can I change my plan at any time?', a: 'Yes. Upgrades take effect immediately. Downgrades apply at the start of your next billing cycle.' },
    { q: 'How do I add agents to my workspace?', a: 'From your workspace settings, navigate to Team Members and click "Invite Agent". They will receive an email invitation.' },
    { q: 'Is my data backed up?', a: 'Yes. We perform daily automated backups with point-in-time recovery available on Growth and Enterprise plans.' },
    { q: 'Can I export my ticket data?', a: 'Yes. You can export tickets as CSV or PDF from the Reports section. The Starter plan and above includes this feature.' },
    { q: 'How do I set up WhatsApp notifications?', a: 'Navigate to Settings > Integrations > WhatsApp and follow the step-by-step connection guide. It takes about 10 minutes.' },
];

function Reveal({ children, className = '', delay = 0 }) {
    return (
        <motion.div className={className}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay }}
        >{children}</motion.div>
    );
}

export default function CustomerSupportPage() {
    useNavigate();
    const cn = 'supportPage';

    const seo = useSEO({
        title:       'Customer Support — We Are Here to Help',
        description: 'Reach the KaizIQ Resolve support team via documentation, email, or live chat. Average first response under 4 hours. 98% customer satisfaction.',
        canonical:   'https://resolve.kaiziq.io/#/support',
        schema: {
            '@context': 'https://schema.org',
            '@type':    'ContactPage',
            name:       'KaizIQ Resolve Support',
            url:        'https://resolve.kaiziq.io/#/support',
            description: 'Get help with KaizIQ Resolve via documentation, email support, or live chat.',
            mainEntity: {
                '@type': 'Organization',
                name:    'KaizIQ Technologies',
                contactPoint: { '@type': 'ContactPoint', email: 'support@kaiziq.com', contactType: 'customer support' },
            },
        },
    });

    return (
        <div className={cn}>
            {seo}
            <TopPanel />

            <section className={cn + '__hero'}>
                <FloatingPaths position={1} />
                <FloatingPaths position={-1} />
                <div className={cn + '__hero__inner'}>
                    <motion.div className={cn + '__hero__tag'} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
                        Customer Support
                    </motion.div>
                    <motion.h1 className={cn + '__hero__title'} initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.1 }}>
                        We're here to help
                    </motion.h1>
                    <motion.p className={cn + '__hero__sub'} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.18 }}>
                        Find answers in our documentation or reach out to the KaizIQ support team directly.
                    </motion.p>
                </div>
            </section>

            {/* Stats */}
            <section className={cn + '__stats'}>
                {[
                    { icon: <Clock size={20} />, value: '< 4 hrs', label: 'Avg first response' },
                    { icon: <Users size={20} />, value: '98%', label: 'Customer satisfaction' },
                    { icon: <Zap size={20} />, value: '24/7', label: 'Docs available' },
                ].map((s, i) => (
                    <Reveal key={i} className={cn + '__stat'} delay={i * 0.1}>
                        <div className={cn + '__stat__icon'}>{s.icon}</div>
                        <div className={cn + '__stat__value'}>{s.value}</div>
                        <div className={cn + '__stat__label'}>{s.label}</div>
                    </Reveal>
                ))}
            </section>

            {/* Support channels */}
            <section className={cn + '__channels'}>
                <Reveal className={cn + '__section-head'}>
                    <h2 className={cn + '__section-title'}>How to reach us</h2>
                </Reveal>
                <div className={cn + '__channels__grid'}>
                    {CHANNELS.map((ch, i) => (
                        <Reveal key={i} className={cn + '__channel' + (ch.soon ? ' ' + cn + '__channel--soon' : '')} delay={i * 0.1}>
                            <div className={cn + '__channel__icon'}>{ch.icon}</div>
                            <h3 className={cn + '__channel__title'}>{ch.title}</h3>
                            <p className={cn + '__channel__desc'}>{ch.desc}</p>
                            <div className={cn + '__channel__availability'}>{ch.availability}</div>
                            <button
                                className={cn + '__channel__cta' + (ch.soon ? ' ' + cn + '__channel__cta--disabled' : '')}
                                onClick={() => {
                                    if (!ch.ctaAction) return;
                                    if (ch.ctaAction === 'docs') window.open('https://docs.kaiziq.io', '_blank');
                                    else window.location.href = ch.ctaAction;
                                }}
                                disabled={ch.soon}
                            >
                                {ch.cta} {!ch.soon && <ArrowRight size={14} />}
                            </button>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* FAQ */}
            <section className={cn + '__faq'}>
                <Reveal className={cn + '__section-head'}>
                    <h2 className={cn + '__section-title'}>Common questions</h2>
                    <p className={cn + '__section-sub'}>Quick answers to the questions we hear most often.</p>
                </Reveal>
                <div className={cn + '__faq__list'}>
                    {FAQS.map((f, i) => (
                        <Reveal key={i} className={cn + '__faq__item'} delay={i * 0.05}>
                            <div className={cn + '__faq__q'}>{f.q}</div>
                            <div className={cn + '__faq__a'}>{f.a}</div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <Footer />
        </div>
    );
}
